// src/pages/ClinicalDataCollector/models/clinicalAiModels.js

// 🔹 Claves estándar para las secciones
export const SECTION_KEYS = {
  HYPERTENSION: 'hypertension',
  CV_RISK: 'cvRisk',
  AF: 'af',
  HEART_FAILURE: 'heartFailure',
  COPD: 'copd',
  ASTHMA: 'asthma',
  OTHER: 'other'
};

/**
 * Diagnóstico detectado en notas y clasificado en sección
 * @typedef {Object} DiagnosisItem
 * @property {string} label           // p.ej. "Mild persistent asthma"
 * @property {string} [icd10]         // p.ej. "J45.30"
 * @property {string} section         // una de SECTION_KEYS.*
 * @property {string} [sourceNoteId]  // id de la nota de donde salió
 * @property {string} [sourceSnippet] // texto corto de contexto
 * @property {number} [confidence]    // 0–1 opcional
 */

/**
 * @typedef {Object} HypertensionSectionData
 * @property {{category:string, count:number}[]} bpDistribution
 * @property {{outcome:string, value:number}[]} nnt
 * @property {string[]} keyRecommendations
 * @property {string[]} sharedDecisionPoints
 */

/**
 * @typedef {Object} CVRiskSectionData
 * @property {number} riskPercent            // 0–100
 * @property {string} statinPlan
 * @property {string[]} additionalTherapies
 */

/**
 * @typedef {Object} AFSectionData
 * @property {string} code
 * @property {string} description
 * @property {string[]} rhythmControl
 * @property {string[]} rateControl
 * @property {string[]} anticoagulation
 * @property {string[]} appendageClosure
 * @property {string[]} benefitConsiderations
 */

/**
 * @typedef {Object} HeartFailureSectionData
 * @property {string} code
 * @property {string} description
 * @property {string} nyhaClass
 * @property {{test:string, result:string, date:string}[]} labs
 * @property {string[]} immunizations
 * @property {string[]} treatment
 * @property {string[]} lifestyle
 */

/**
 * @typedef {Object} COPDSectionData
 * @property {string} code
 * @property {string} description
 * @property {string} goldClassification
 * @property {{measure:string, value:string, date:string}[]} spirometry
 * @property {string[]} immunizations
 * @property {string[]} maintenanceTherapy
 * @property {{count:number, lastDate:string}} exacerbationHistory
 * @property {string[]} nonPharma
 */

/**
 * @typedef {Object} AsthmaSectionData
 * @property {string} code
 * @property {string} description
 * @property {string} severity
 * @property {{ACT?:number, ACQ?:number, [key:string]:number}} controlScores
 * @property {{measure:string, value:string, date:string}[]} spirometry
 * @property {string[]} maintenanceTherapy
 * @property {string[]} rescueTherapy
 * @property {string[]} triggers
 * @property {string[]} managementPlans
 */

/**
 * @typedef {Object} ClinicalAiSectionsSummary
 * @property {boolean} hypertension
 * @property {boolean} cvRisk
 * @property {boolean} af
 * @property {boolean} heartFailure
 * @property {boolean} copd
 * @property {boolean} asthma
 */

/**
 * @typedef {Object} ClinicalAiSectionsData
 * @property {HypertensionSectionData | null} [hypertension]
 * @property {CVRiskSectionData | null} [cvRisk]
 * @property {AFSectionData | null} [af]
 * @property {HeartFailureSectionData | null} [heartFailure]
 * @property {COPDSectionData | null} [copd]
 * @property {AsthmaSectionData | null} [asthma]
 */

/**
 * Respuesta completa del endpoint de IA
 * @typedef {Object} ClinicalAiPanelsResponse
 * @property {string} patientId
 * @property {string} generatedAt  // ISO date
 * @property {DiagnosisItem[]} diagnoses
 * @property {ClinicalAiSectionsSummary} sectionsSummary
 * @property {ClinicalAiSectionsData} sectionsData
 */

// 👇 Sólo exportamos constantes, los typedefs viven en los comentarios
export const __CLINICAL_AI_MODELS__ = true;
